import { useApolloClient } from "@apollo/client/react";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import { PLAYBACK_SESSION_CHECK_MUTATION } from "@/apollo/queries/playback";
import {
  useAddResumeMutation,
  useFullscreenApi,
  usePlaybackSessionCloseMutation,
} from "@/hooks";
import config from "@/lib/config";
import debugLog from "@/lib/debugLog";
import {
  PlalaBeaconError,
  UNSUPPORTED_SCHEME_ERROR_MESSAGE,
} from "@/lib/errors";
import getVisitorId from "@/lib/getVisitorId";
import maybeIOS from "@/lib/maybeIOS";
import { liveStatus } from "@/lib/metaUtil";

const beaconInterval = 30 * 1000;

export function usePlayerEventHandler(
  user,
  userLoading,
  deviceInfo,
  player,
  meta,
  entitlements,
  playbackAuth,
  playToken,
  displayOverlay,
  canplay,
  callbacks = {}
) {
  const client = useApolloClient();
  const [handled, setHandled] = useState(false);
  const resumeSending = useRef(false);
  const mounted = useRef(false);
  const metaRef = useRef();
  const vuid = useRef();
  const playingIntervalId = useRef();
  const beaconIntervalId = useRef();
  const seeking = useRef(false);
  const playTimePercentage = useRef(0);
  const playbackAuthRef = useRef();
  const playTokenRef = useRef();
  const fsAPI = useFullscreenApi();
  const [addResumeMutation, { loading }] = useAddResumeMutation();
  const [sessionCloseMutation] = usePlaybackSessionCloseMutation();
  const router = useRouter();

  playbackAuthRef.current = playbackAuth;
  playTokenRef.current = playToken;

  const sendResume = useCallback(async () => {
    if (resumeSending.current) return;
    if (loading) return;
    // isLiveだけだとfirstplayの時にfalseで返ってくるのでdurationが取れるかどうかも判定する
    if (player && isNaN(player?.duration())) return;
    if (player && player?.isLive()) return;
    const meta = metaRef.current;
    const psid = playbackAuthRef.current?.log_params?.psid;
    if (user && meta && player && psid) {
      resumeSending.current = true;
      const resume = player?.isLive()
        ? -1
        : parseInt(player?.currentTime(), 10);
      const reachToEnd = player?.ended() ? true : false;
      await addResumeMutation({
        variables: {
          resume: resume,
          metaId: Number(meta.id),
          groupedMetaId: meta.tvShow ? Number(meta.tvShow?.id) : null,
          psid,
          reachToEnd,
        },
      });
      resumeSending.current = false;
    }
  }, [resumeSending, loading, user, player, addResumeMutation]);

  useEffect(() => {
    const handler = () => {
      setHandled(false);
      sendResume();
    };
    router.events.on("routeChangeStart", handler);
    return () => {
      router.events.off("routeChangeStart", handler);
    };
  }, [router.events, sendResume]);

  const logTrack = useCallback(
    (event, additionalParams = {}) => {
      // log_params:
      //   a: 4
      //   aid: 17
      //   cp: 6
      //   dc: 1
      //   fl: 1
      //   media_id: 8
      //   meta_id: 195
      //   psgk: "default"
      //   psid: "bf57765ffa0b4c069d61e1eedd05f675"
      //   service: "spod"
      //   uid: 17
      const meta = metaRef.current;
      const logParams = playbackAuthRef.current?.log_params;
      if (["pause", "ended", "dismiss", "dispose", "error"].includes(event)) {
        clearInterval(playingIntervalId.current);
        playingIntervalId.current = null;
      }

      const episodeType = ((meta) => {
        if (meta.is_live) {
          if (liveStatus(meta)) {
            return "live";
          } else if (meta.catch_up_flag) {
            return "catchup";
          }
        }
        return "vod";
      })(meta);

      const params = {
        front_id: config.frontId,
        episode_type: episodeType,
        s: parseInt(player?.currentTime(), 10),
        ts: +new Date(),
        vuid: vuid.current,
        ...logParams,
        ...additionalParams,
      };

      if (event !== "beacon") {
        params.event = event;
      }

      if (["pause", "dismiss", "ended", "dispose"].includes(event)) {
        params.pt = player?.currentTime();
      }

      if (user?.currentProfile?.id) {
        params.profile_id = user.currentProfile.id;
      }

      if (user && user.test_flg) {
        params.fl = 1;
      }

      const rightIds = entitlements.map((entitle) => entitle.right_id);
      if (rightIds.length > 0) {
        params.right_id = rightIds.join(",");
      }

      const url = (() => {
        if (event === "beacon") {
          return `${config.logTrack.url}/beacon`;
        } else {
          return `${config.logTrack.url}/track`;
        }
      })();
      debugLog(event, params);
      const qs = new URLSearchParams(params);
      return fetch(`${url}?${qs}`).catch((e) => console.error(e));
    },
    [user, entitlements, player, vuid]
  );

  const plalaBeacon = useCallback(async () => {
    if (playTokenRef.current) {
      const { url, clientId } = config.plalaBeacon;
      const body = JSON.stringify({
        play_token: playTokenRef.current,
        stop_position: Math.floor(player?.currentTime()),
      });
      fetch(url, {
        method: "POST",
        mode: "cors",
        body,
        headers: { "Content-Type": "application/json", "X-Client": clientId },
      })
        .then((res) => res.json())
        .then((res) => {
          // console.log(res);
          // TODO: レスポンスをみて再生を止める必要がある
          // 1002103
          // 1002201
          // {
          //   "status": "NG",
          //   "errorno": "1002103"
          // }
          const stopStatus = ["1002103", "1002201"];
          if (res.status === "NG" && stopStatus.includes(res?.errorno)) {
            player?.dispose();
            const error = new PlalaBeaconError(null, res.errorno);
            displayOverlay({ reason: "error", error });
          }
        })
        .catch((e) => {
          console.error(e);
          player?.dispose();
          const error = new PlalaBeaconError(null, "9999");
          displayOverlay({ reason: "error", error });
          window.plugin.fireError(
            9999,
            error.message + "（バリュープランポーリングエラーの可能性あり）"
          );
        });
    }
  }, [player, displayOverlay]);

  const sessionCheck = useCallback(() => {
    client
      .mutate({
        mutation: PLAYBACK_SESSION_CHECK_MUTATION,
        variables: { sessionId: playbackAuthRef.current.playback_session_id },
      })
      .then(({ data }) => {
        if (!data?.playbackSessionCheck?.result) {
          window.location.reload();
        }
      })
      .catch((e) => {
        // サーバーが落ちた場合、networkErrorで返ってくる
        if (e.networkError) {
          window.plugin.fireError(
            99999,
            UNSUPPORTED_SCHEME_ERROR_MESSAGE[99999]
          );
        }
        console.error(e);
        window.location.reload();
      });
  }, [client]);

  const sessionClose = useCallback(async () => {
    if (playbackAuth.current && playbackAuthRef.current?.playback_session_id) {
      await sessionCloseMutation({
        variables: { sessionId: playbackAuthRef.current.playback_session_id },
      });
    }
  }, [playbackAuth, sessionCloseMutation]);

  // イベントハンドリング
  // beacon 視聴履歴、ブックマーク
  // paraviをみるとon playでplaybacksession checkをコールして invalid_grantになったらリロードしてた
  // セッションを取得した直後にチェックが必要なのか？
  const eventHandler = useCallback(() => {
    function execCallback(event) {
      if (callbacks[event]) {
        callbacks[event]();
      }
    }

    player?.on("loadstart", () => {
      // 既存コードではEdgeでDRMがある場合playreadyに対応しているか確認している
      execCallback("loadstart");
    });

    player?.on("canplay", () => {
      // 既存コードではvideoタグにtitleを設定してstate canplayをtrueにしている
      execCallback("canplay");
    });

    player?.on("firstplay", () => {
      logTrack("firstplay");
      sendResume();
      execCallback("firstplay");
    });

    player?.on("play", () => {
      // beacon startを送信
      // audienceLog Playを送信
      logTrack("play");
      execCallback("play");
      // playback/sessionをチェック
      sessionCheck();
    });

    player?.on("playing", () => {
      // beacon playingを30secごとに送信
      if (!playingIntervalId.current) {
        playingIntervalId.current = setInterval(() => {
          logTrack("beacon");
        }, beaconInterval);
      }
      if (!beaconIntervalId.current) {
        beaconIntervalId.current = setInterval(() => {
          plalaBeacon();
        }, beaconInterval);
      }
      execCallback("playing");
      // audienceLog Playingを30secごとに送信
    });

    player?.on("pause", () => {
      // 後追いなしの場合は停止させない
      if (player?.isLive() && meta.is_live && !meta.start_over_flag) {
        player?.play();
      } else if (!player?.ended() && !player?.seeking()) {
        // audienceLog Pauseを送信
        // beacon Pause
        logTrack("pause");
        sendResume();
      }
      execCallback("pause");
    });

    player?.on("timeupdate", () => {
      // currentTimeがdurationの何％かチェックして
      // 25%,50%,75%のときにmilestoneイベントを送る
      // シークでもどった時には発火しないようにする
      // シークで一気に進んだ場合は全部送る
      const currentTime = player?.currentTime();
      const duration = player?.duration();
      [25, 50, 75]
        .filter((per) => per > playTimePercentage.current)
        .forEach((per) => {
          // 複数回実行されないために、ここでもう一度filterで使った条件をいれる
          if (
            per > playTimePercentage.current &&
            currentTime > duration * (per / 100)
          ) {
            playTimePercentage.current = per;
            logTrack("milestone", { percentage: per });
          }
        });
      execCallback("timeupdate");
    });

    player?.on("seeking", () => {
      // 既存ではwaitingRetryという処理を実行していた
      // 今回必要になるか不明
      if (!seeking.current) {
        seeking.current = true;
        logTrack("seekStart");
      }
      debugLog("seeking");
      execCallback("seeking");
    });

    player?.on("seeked", () => {
      // 既存では以下の問題があったようだがどうなのか
      // iOSのfiarplayだとシーク後に音声のみになってしまう
      seeking.current = false;
      logTrack("seekEnd");
      debugLog("seeked");
      execCallback("seeked");
    });

    player?.on("waiting", () => {
      // 既存では以下の問題があったようだがどうなのか
      // AESのライブがm3u8にEndListがなく、keyリクエストが404になるだけで再生が止められないので
      // LIVEでwaitingが発火したときに配信期間が過ぎていたら再生を止める
      debugLog("waiting");
      execCallback("waiting");
    });

    player?.on("renditionswitch", () => {
      debugLog("renditionswitch", player?.rendition());
    });

    player?.on("ended", () => {
      // beacon End
      logTrack("ended");
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      // audienceLog Endを送信
      sendResume();
      execCallback("ended");
    });

    player?.on("suspend", () => {
      logTrack("suspend");
      execCallback("suspend");
      debugLog("suspend");
    });

    player?.on("dispose", () => {
      logTrack("dispose");
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      execCallback("dispose");
      sendResume();
    });

    player?.on("error", () => {
      clearInterval(playingIntervalId.current);
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      logTrack("error");
      sendResume();
      sessionClose();
      execCallback("error");
      // beacon stopを送信
      // audienceLog Stopを送信
    });
  }, [
    player,
    callbacks,
    meta,
    plalaBeacon,
    logTrack,
    sendResume,
    sessionCheck,
    sessionClose,
  ]);

  useEffect(() => {
    mounted.current = true;
    getVisitorId().then((id) => (vuid.current = id));
    // watchページないで別のwatchページに遷移した時に、ページ遷移する前にmetaだけ更新されてレジュームが適切に送信されないので
    // refでメタをもっておいて更新されないようにする
    metaRef.current = meta;
    return () => {
      mounted.current = false;
    };
  }, [meta]);

  useEffect(() => {
    let hidden, visibilityChange;
    if (typeof document.hidden !== "undefined") {
      hidden = "hidden";
      visibilityChange = "visibilitychange";
    } else if (typeof document.msHidden !== "undefined") {
      hidden = "msHidden";
      visibilityChange = "msvisibilitychange";
    } else if (typeof document.webkitHidden !== "undefined") {
      hidden = "webkitHidden";
      visibilityChange = "webkitvisibilitychange";
    }

    const handlePageHide = () => {
      sendResume();
    };

    const handleVisibilityChange = () => {
      if (document[hidden]) {
        handlePageHide();
      } else {
        // 再生中の場合、playback/sessionをチェック
        if (playingIntervalId.current) {
          sessionCheck();
        }
      }
    };

    const handleBeforeUnload = () => {
      logTrack("dismiss");
    };

    const handleFullscreenChange = () => {
      if (player && !player?.ended()) {
        if (player?.isFullscreen()) {
          logTrack("fullscreen");
        } else {
          logTrack("exitFullscreen");
        }
      }
    };

    window.addEventListener(visibilityChange, handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);
    const isIOS = maybeIOS(deviceInfo);
    if (isIOS || (deviceInfo?.isMac && deviceInfo?.isSafari)) {
      window.addEventListener("pagehide", handlePageHide);
      router.beforePopState(({}) => close);
    }
    document.addEventListener(fsAPI.fullscreenchange, handleFullscreenChange);

    return () => {
      clearInterval(playingIntervalId.current);
      clearInterval(beaconIntervalId.current);
      beaconIntervalId.current = null;
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener(visibilityChange, handleVisibilityChange);
      if (isIOS || (deviceInfo?.isMac && deviceInfo?.isSafari)) {
        window.removeEventListener("pagehide", handlePageHide);
        router.beforePopState(() => true);
      }
      document.removeEventListener(
        fsAPI.fullscreenchange,
        handleFullscreenChange
      );
    };
  }, [deviceInfo, player, fsAPI, logTrack, router, sendResume, sessionCheck]);

  useEffect(() => {
    if (
      !handled &&
      player &&
      playbackAuthRef.current &&
      !userLoading &&
      canplay
    ) {
      setHandled(true);
      eventHandler();
    }
    return () => {
      if (!mounted.current) {
        sendResume();
      }
    };
  }, [handled, player, userLoading, canplay, eventHandler, sendResume]);
}
